type DomEventTarget = {
  addEventListener: (
    event: string,
    listener: EventListener,
    options?: AddEventListenerOptions | boolean,
  ) => void;
  removeEventListener: (event: string, listener: EventListener) => void;
};

const attach = <EventType extends Event>(
  target: DomEventTarget,
  event: string,
  listener: (event: EventType) => void,
  options?: AddEventListenerOptions | boolean,
): (() => void) => {
  const eventListener = listener as EventListener;
  target.addEventListener(event, eventListener, options);
  return () => target.removeEventListener(event, eventListener);
};

export const attachBrowserBack = (
  target: DomEventTarget,
  listener: EventListener,
): (() => void) =>
  /* action-id: lifecycle.browser.back */ attach(target, "popstate", listener);

export const attachSearchKeyboard = (
  target: DomEventTarget,
  listener: (event: KeyboardEvent) => void,
): (() => void) =>
  /* action-id: lifecycle.search.keyboard */ attach(
    target,
    "keydown",
    listener,
  );

export const attachModalPointerDismiss = (
  target: DomEventTarget,
  listener: (event: MouseEvent) => void,
): (() => void) =>
  /* action-id: lifecycle.modal.dismiss */ attach(
    target,
    "mousedown",
    listener,
  );

export const attachModalKeyboardDismiss = (
  target: DomEventTarget,
  listener: (event: KeyboardEvent) => void,
): (() => void) =>
  /* action-id: lifecycle.modal.dismiss */ attach(target, "keydown", listener);

export const attachViewportResize = (
  target: DomEventTarget,
  listener: EventListener,
): (() => void) =>
  /* action-id: lifecycle.viewport.resize */ attach(
    target,
    "resize",
    listener,
    { passive: true },
  );

export const canStartModalSwipe = (input: {
  platform: string;
  shouldPersist: boolean;
  canScrollUp: boolean;
}): boolean =>
  input.platform === "ios" && !input.shouldPersist && !input.canScrollUp;

export const getModalSwipeEnd = (translateY: number): "dismiss" | "reset" =>
  translateY > 100 ? "dismiss" : "reset";
